import { BrowserClient, Scope, browserTracingIntegration, getDefaultIntegrations } from "@sentry/browser";
import { getClient } from "@sentry/core";
import { reactRouterBrowserTracingIntegration, wrapUseRoutes as wrapUseRoutesCompat } from "@sentry/react";
import { useEffect } from "react";
import { matchRoutes, useLocation, useNavigationType, createRoutesFromChildren, useRoutes } from "react-router";
/**
 * EVERY client gets its own freshly built instances: several defaults keep per-instance state with
 * no client guard (`Dedupe` above all), so sharing them makes the second target of a dual-send drop
 * the event. The SDK's global unhandled-error integrations are stripped — `globalHandlers.ts` owns
 * that centrally.
 *
 * `routerTracing: false` swaps the react-router v6 integration for plain browser tracing: it is the
 * only opt-out for apps on a router the v6 instrumentation cannot read (React Router 7 data mode),
 * which would otherwise produce no transactions at all instead of pageload/navigation ones.
 */
const integrations = ({ routerTracing = true }) => [
    ...getDefaultIntegrations({}).filter((defaultIntegration) => !["BrowserApiErrors", "GlobalHandlers"].includes(defaultIntegration.name)),
    routerTracing
        ? reactRouterBrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        })
        : browserTracingIntegration(),
];
/**
 * Sentry's `wrapUseRoutes` (version-neutral compat export) reads module-level globals that only
 * the browser tracing integration's `setup(client)` assigns, so it MUST be resolved lazily on
 * first render: called any earlier it returns the original hook permanently and web router
 * tracing never runs.
 *
 * The resolution is then FROZEN and never re-evaluated — the SDK's wrapper creates an element while
 * the fallback is a hook, so swapping between renders remounts the route subtree and changes the
 * consumer's hook count. Two residuals are accepted as a result:
 * 1. A tree first rendered before any client set the integration up stays untraced.
 * 2. A tree first rendered while its own client is not the current one stays untraced for good.
 */
const wrapUseRoutes = (origUseRoutes, getBoundClient) => {
    let resolved;
    const resolve = () => {
        const boundClient = getBoundClient?.();
        if (boundClient && boundClient !== getClient()) {
            return origUseRoutes;
        }
        return wrapUseRoutesCompat(origUseRoutes);
    };
    return (routes, locationArg) => {
        resolved ??= resolve();
        return resolved(routes, locationArg);
    };
};
const wrapComponent = (Component) => Component;
// Every app builds and inits its own `BrowserClient`, so there is no process-wide binding to share.
const nativePlatform = false;
// Native-only concern: the active-app tag attributes native-layer crashes, which don't occur on web.
const setActiveAppTag = (_projectId) => { };
// Mirrors `Platform.OS` without importing `react-native`, so the web graph stays free of it.
const platformOS = "web";
export { BrowserClient as Client, Scope, integrations, nativePlatform, platformOS, setActiveAppTag, useRoutes, wrapComponent, wrapUseRoutes, };
